import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { distinctUntilChanged, filter, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostAccounting } from '../../../models/cost-accounting.model';
import { CostAccountingStateEnum } from '../../../models/enum/cost-accounting-state.enum';
import { ICostAccountingLayout } from '../../../models/layout/cost-accounting-layout.model';
import { ICostAccountingState } from '../../../services/facades/cost-accounting/cost-accounting-state.model';
import { CostAccountingFacade } from '../../../services/facades/cost-accounting/cost-accounting.facade';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostAccountingLayoutService } from '../../../services/layout/cost-accounting-layout.service';
import { isOverlapping, stringToMoment } from '../../../utils/moment-utils';
import { CostAccountingDialogComponent } from './cost-accounting-dialog/cost-accounting-dialog.component';

@Component({
    templateUrl: './cost-accounting-page.html',
    styleUrls: [ './cost-accounting-page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostAccountingPage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator!: MatPaginator;

    public costAccountingStateProgress: Record<string, number> = {};

    public layout: ICostAccountingLayout;
    public filterFormGroup = this.myFormBuilder.group({
        dateFilter: this.myFormBuilder.group({
            start: null as Moment,
            end: null as Moment,
        }),
        typeFilter: null as number
    });

    public filterValue: string = '';
    public dataSource: MatTableDataSource<ICostAccounting> = new MatTableDataSource<ICostAccounting>([]);
    public costAccountingState$: Observable<ICostAccountingState>;
    public sharedDataState$: Observable<ISharedDataState>;

    private destroy$: Subject<void> = new Subject();

    constructor(private myCostAccountingFacade: CostAccountingFacade,
                private mySharedDataFacade: SharedDataFacade,
                private myMatDialog: MatDialog,
                private myCostAccountingLayoutService: CostAccountingLayoutService,
                private myFormBuilder: FormBuilder) {
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myCostAccountingFacade.clearState();
    }

    public applyFilter(filterValue: string): void {
        this.filterValue = filterValue.trim().toLowerCase();
        this.dataSource.filter = 'TEST'; // default value to trigger method
    }

    public ngOnInit(): void {
        this.layout = this.myCostAccountingLayoutService.getCostAccountingLayout();
        this.costAccountingState$ = this.myCostAccountingFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.createStateDictionary();
        this.initializeFilter();
        this.costAccountingState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ICostAccountingState) => s.data),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostAccounting[]) => {
                this.dataSource.data = data;
            });
        this.dataSource.sortingDataAccessor = (costAccounting: ICostAccounting, tableColumnName: string): string | number => {
            switch (tableColumnName) {
                case 'costAccountingType':
                    return costAccounting.costAccountingType.name;
                case 'from':
                    return costAccounting.dateRange.from.valueOf();
                case 'to':
                    return costAccounting.dateRange.to.valueOf();
                default:
                    return costAccounting[ tableColumnName ];
            }
        };

        this.dataSource.filterPredicate = (costAccounting: ICostAccounting, _filter: string): boolean => {
            const filterValues = this.filterFormGroup.getRawValue();
            const filteredString = this.filterValue.trim().toLowerCase();
            // string-filter
            const matchString = isNullOrUndefined(filteredString) || costAccounting.name.trim().toLowerCase().includes(filteredString)
                || costAccounting.costAccountingType.name.trim().toLowerCase().includes(filteredString);
            // typ-filter
            const matchTyp = isNullOrUndefined(filterValues.typeFilter) || filterValues.typeFilter === costAccounting.costAccountingType.id;
            // date-filter
            const matchDate = isNullOrUndefined(filterValues.dateFilter.start)
                || isOverlapping(new DateRange<Moment>(filterValues.dateFilter.start, filterValues.dateFilter.end),
                    new DateRange<Moment>(stringToMoment(costAccounting.dateRange.from), stringToMoment(costAccounting.dateRange.to)));

            return matchString && matchTyp && matchDate;
        };

        void firstValueFrom(this.mySharedDataFacade.fetchCostAccountingTypes());
        void this.myCostAccountingFacade.fetchData();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public openDialog(costAccounting?: ICostAccounting): void {
        const dialog: MatDialogRef<CostAccountingDialogComponent> = this.myMatDialog.open(CostAccountingDialogComponent, {
            width: '880px',
            minHeight: '505px',
            data: costAccounting,
        });
        dialog.afterClosed().subscribe(() => {
            void firstValueFrom(this.myCostAccountingFacade.fetchCostAccountings(undefined, undefined, undefined, true));

        });
    }

    public initializeFilter(): void {
        this.filterFormGroup.valueChanges.pipe(
            filter(v => this.isDatefilterValid(v.dateFilter.start, v.dateFilter.end)),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe(() => {
                // Trigger filter manually; The values are in the formGroup
                this.dataSource.filter = 'TEST';
            });
    }

    private isDatefilterValid(start: Moment, end: Moment): boolean {
        if (!isNullOrUndefined(start) && !isNullOrUndefined(end) && start.isAfter(end)) {
            return false;
        }
        /* Returns true if either both are undefined or both are set */
        return !((isNullOrUndefined(start) && !isNullOrUndefined(end)) || (isNullOrUndefined(end) && !isNullOrUndefined(start)));
    }

    private createStateDictionary(): void {
        this.costAccountingStateProgress[ CostAccountingStateEnum.INITIALIZED ] = 0;
        this.costAccountingStateProgress[ CostAccountingStateEnum.COST_TYPE_ACCOUNTING ] = 1;
        this.costAccountingStateProgress[ CostAccountingStateEnum.COST_CENTER_APPORTIONMENTS ] = 2;
        this.costAccountingStateProgress[ CostAccountingStateEnum.COST_UNIT_ACCOUNTING ] = 3;
    }
}
